import React, { useEffect, useRef, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import './PersonnelDialogStyle.scss';
import _ from 'lodash';
import useFetch from 'use-http';
import CondensedPersonnel from 'src/models/personnel/CondensedPersonnel';
import TSDILandman from 'src/models/personnel/TSDILandman';
import LoadingSpinner from 'src/components/common/LoadingSpinner';

interface IPersonnelAdvancedQuickFindProps {
    dialogName: string;
    selectedPersonnel: TSDILandman;
    hideDialog: () => void;
    selectPersonnel?: (personnel: TSDILandman) => void;
}

const PersonnelAdvancedQuickFind = (props: IPersonnelAdvancedQuickFindProps) => {
    const { get, response } = useFetch();
    const [selectedId, setSelectedId] = useState<number>(undefined); // selected id from results (lmNo)
    const [personnelList, setPersonnelList] = useState<Array<CondensedPersonnel>>();
    const inputRef = useRef<HTMLInputElement>(null);
    const searchTerm = useRef<string>();
    const searchCategory = useRef<string>(); // selected category from dropdown
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const latestSearchId = useRef(0); // tracker to prevent race conditions

    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (selectedId !== undefined && selectedId !== null) {
            handleSelectRow(selectedId);
        }
    }, [selectedId]);

    const onCreate = () => {
        console.log('PersonnelAdvancedQuickFind onCreate()');
    };

    const onDestroy = () => {
        console.log('PersonnelAdvancedQuickFind onDestroy()');
    };

    // selection of row via click
    const handleSelectRow = async (id: number) => {
        let result = await get(`v1.0/landmen/personnel/${id}/get-by-lm-no`);

        if (response.ok && result) {
            props.selectPersonnel(result);
            props.hideDialog();
            setPersonnelList(null);
        } else {
            console.log('unable to find personnel');
        }
    };

    // searching
    const handleSearch = _.debounce(async (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const term = (document.getElementById('userSearch') as HTMLInputElement).value?.toLowerCase();
        searchTerm.current = term;

        if (!term || term.length === 1) {
            ++latestSearchId.current;
            setPersonnelList(null);
            setIsLoading(false);
            return;
        }

        const filterValue = (document.getElementById('findByOption') as HTMLSelectElement).value;
        searchCategory.current = filterValue;

        const requestId = ++latestSearchId.current;
        setIsLoading(true);
        setPersonnelList(null);

        let results = await get(`v1.0/landmen/personnel/get-by-category?category=${encodeURIComponent(searchCategory.current)}&term=${encodeURIComponent(searchTerm.current)}`);

        if (requestId !== latestSearchId.current) {
            return;
        }

        if (response.ok && results) {
            setPersonnelList(results);
        } else {
            setPersonnelList(null);
        }
        setIsLoading(false);
    }, 800);

    const handleClose = () => {
        setPersonnelList(null);
        setSelectedId(undefined);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        props.hideDialog();
    };

    return (
        <Dialog title="Advanced Quick Find" dialogName={props.dialogName} bodyStyle={{ width: '75%', height: '75%' }} onCloseDialog={handleClose}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="dialog-form-row">
                    <div className="dlc-form-textbox-wrapper2">
                        <label>Find:
                            <input
                                type="search"
                                name="userSearch"
                                id="userSearch"
                                onChange={handleSearch}
                                ref={inputRef}
                                style={{ width: '45%', marginLeft: '4px', marginRight: '1rem', border: '1px solid #6393C1' }}
                            />
                        </label>
                    </div>
                    <div className="dlc-form-textbox-wrapper2">
                        <label style={{ verticalAlign: 'center' }}>Find By:
                            <select name={'findByOption'} id={'findByOption'} onChange={handleSearch} style={{ width: '45%', marginLeft: '4px', marginRight: '1rem', border: '1px solid #6393C1' }}>
                                <option id="findBy.id">Employee Id</option>
                                <option id="findBy.fullName">Full Name</option>
                                <option id="findBy.fName">First Name</option>
                                <option id="findBy.lName" selected={true}>Last Name</option>
                                <option id="findBy.email">Email Address</option>
                                <option id="findBy.corporationName">Corporation Name</option>
                                <option id="findBy.ssn">Social Security Number</option>
                                <option id="findBy.ein">Federal Tax Id</option>
                            </select>
                        </label>
                    </div>
                </div>
                <br />
                <div style={{ flex: 1, maxHeight: '50vh', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }} id="personnelTable">
                        <thead style={{ zIndex: 100 }}>
                            <tr style={{ width: '100%' }}>
                                <th className="table-header" style={{ width: '8%' }}>No</th>
                                <th className="table-header" style={{ width: '10%' }}>Last Name</th>
                                <th className="table-header" style={{ width: '10%' }}>First Name</th>
                                <th className="table-header" style={{ width: '6%' }}>Middle</th>
                                <th className="table-header" style={{ width: '4%' }}>DNC?</th>
                                <th className="table-header" style={{ width: '7%' }}>City</th>
                                <th className="table-header" style={{ width: '4%' }}>State</th>
                                <th className="table-header" style={{ width: '5%' }}>Zip</th>
                                <th className="table-header" style={{ width: '15%' }}>Email</th>
                                <th className="table-header" style={{ width: '8%' }}>Social Security Number</th>
                                <th className="table-header" style={{ width: '8%' }}>Federal Tax ID (EIN)</th>
                                <th className="table-header" style={{ width: '15%' }}>Corporation Name</th>
                            </tr>
                        </thead>
                        {isLoading && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '50vh',
                                    width: '100%',
                                }}
                            >
                                <LoadingSpinner />
                            </div>
                        )}
                        <br />
                        <tbody>
                            {
                                personnelList?.map((p: CondensedPersonnel, index) => (
                                    <tr
                                        key={p.tsdNo}
                                        onClick={() => setSelectedId(p.lmNo)}
                                        style={{ width: '100%', backgroundColor: props.selectedPersonnel?.lmNo === p?.lmNo ? 'yellow' : undefined }}
                                    >
                                        <td style={{ width: '8%', border: '1px solid black' }}>{p.tsdNo}</td>
                                        <td style={{ width: '10%', border: '1px solid black' }}>{p.lastName}</td>
                                        <td style={{ width: '10%', border: '1px solid black' }}>{p.firstName}</td>
                                        <td style={{ width: '6%', border: '1px solid black' }}>{p.middleName}</td>
                                        <td style={{ width: '4%', border: '1px solid black' }}>{p.dnc ? 'x' : ''}</td>
                                        <td style={{ width: '7%', border: '1px solid black' }}>{p.city ? p.city : ''}</td>
                                        <td style={{ width: '4%', border: '1px solid black' }}>{p.state ? p.state : ''}</td>
                                        <td style={{ width: '5%', border: '1px solid black' }}>{p.zipCode ? p.zipCode : ''}</td>
                                        <td style={{ width: '15%', border: '1px solid black' }}>{p.email}</td>
                                        <td style={{ width: '8%', border: '1px solid black' }}>{p.ssn}</td>
                                        <td style={{ width: '8%', border: '1px solid black' }}>{p.federalTaxId}</td>
                                        <td style={{ width: '15%', border: '1px solid black' }}>{p.corporationName}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '25%' }}
                    text={'Close'}
                    onClick={() => handleClose()}
                    intent="primary"
                />
            </div>
        </Dialog>
    );
};

export default PersonnelAdvancedQuickFind;


// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/PersonnelAdvancedQuickFindDialog.tsx